import { useRef, useState } from 'react';
import { Download, Loader2, PiggyBank, Send } from 'lucide-react';
import { HeroAction } from '@/components/ui/hero-action';
import { t } from '@/lib/i18n/messages';
import { submitJuthoorInitiative, getJuthoorParticipants } from '@/lib/api/juthoor';
import { exportElementToPdf } from '@/lib/utils/pdf-export';
import { formatDate } from '@/lib/utils/format';
import { useToast } from '@/components/ui/toast';
import { JuthoorSetBudgetModal } from './juthoor-set-budget-modal';
import { JuthoorFarmDataReport } from './juthoor-farm-data-report';
import type { Locale } from '@/types/auth';
import type { JuthoorInitiative, JuthoorInitiativeStats, JuthoorParticipantEntry } from '@/types/juthoor';

export interface JuthoorHeroActionsProps {
  initiative: JuthoorInitiative;
  locale: Locale;
  stats: JuthoorInitiativeStats | null;
  onRefetch: () => void;
}

/**
 * Juthoor-specific hero action buttons (Set Budget / Submit for Approval / Export Farm Data),
 * rendered alongside the generic Edit/Fund/Invite actions other initiative types use.
 */
export function JuthoorHeroActions({ initiative, locale, stats, onRefetch }: JuthoorHeroActionsProps) {
  const isRTL = locale === 'ar';
  const { toast } = useToast();

  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportParticipants, setExportParticipants] = useState<JuthoorParticipantEntry[]>([]);
  const reportRef = useRef<HTMLDivElement>(null);

  const isRejected = initiative.status === 'municipality_rejected';
  const isDraft = initiative.status === 'draft';
  const isEditable = isDraft || isRejected;
  const hasBudget = initiative.juthoor.budget != null && initiative.juthoor.budget > 0;

  const treesPlanted = stats?.trees_planted ?? 0;
  const co2Kg = Math.round(treesPlanted * 21);
  const o2Kg = Math.round(treesPlanted * 118);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const response = await submitJuthoorInitiative(initiative.id);
      if (response.success) {
        onRefetch();
      } else {
        toast(response.message || t(locale, 'error'), 'error');
      }
    } catch {
      toast(t(locale, 'error'), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleExportFarmData() {
    setExporting(true);
    try {
      // Fetch the full participant list — the on-screen ledger is paginated (20/page)
      const response = await getJuthoorParticipants(initiative.id, { page: 1, per_page: 10_000 });
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to fetch participants');
      }
      const payload = response.data as unknown as { data: JuthoorParticipantEntry[] } | JuthoorParticipantEntry[];
      const allParticipants = Array.isArray(payload) ? payload : payload.data;

      // Populate the hidden report with full data, then wait for it to paint before capturing
      setExportParticipants(allParticipants);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

      if (reportRef.current) {
        await exportElementToPdf(reportRef.current, `juthoor-farm-data-${initiative.id}.pdf`);
      }
    } catch {
      toast(t(locale, 'error'), 'error');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      {isEditable && (
        <HeroAction
          icon={PiggyBank}
          label={hasBudget ? (isRTL ? 'تعديل الميزانية' : 'Edit Budget') : (isRTL ? 'تعيين الميزانية' : 'Set Budget')}
          onClick={() => setShowBudgetModal(true)}
        />
      )}
      {isEditable && (
        <HeroAction
          icon={Send}
          label={submitting ? t(locale, 'loading') : t(locale, 'submitForApproval')}
          onClick={handleSubmit}
          disabled={submitting || !hasBudget}
          primary
        />
      )}
      <HeroAction
        icon={exporting ? Loader2 : Download}
        label={isRTL ? 'تصدير البيانات' : 'Export Farm Data'}
        onClick={handleExportFarmData}
        disabled={exporting}
      />

      {showBudgetModal && (
        <JuthoorSetBudgetModal
          initiative={initiative}
          locale={locale}
          onClose={() => setShowBudgetModal(false)}
          onSuccess={() => {
            setShowBudgetModal(false);
            onRefetch();
          }}
        />
      )}

      {/* Off-screen report captured to PDF on export — never shown to the user */}
      <div style={{ position: 'fixed', top: 0, insetInlineStart: -10000, zIndex: -1 }} aria-hidden="true">
        <JuthoorFarmDataReport
          ref={reportRef}
          initiative={initiative}
          locale={locale}
          landName={initiative.juthoor.land_name ?? null}
          treesReserved={stats?.trees_reserved ?? 0}
          treesPlanted={treesPlanted}
          treesRewarded={stats?.trees_rewarded ?? 0}
          co2Kg={co2Kg}
          o2Kg={o2Kg}
          participants={exportParticipants}
          generatedAt={formatDate(new Date().toISOString(), locale, 'long')}
        />
      </div>
    </>
  );
}
